import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { collection, deleteDoc, doc, Timestamp, writeBatch } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';

import { db, storage } from '../../config/firebase.js';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import content from '../AboutUsPage/data/aboutUsContent.json';
import ConfirmDialog from '../../utils/ConfirmDialog';
import Toast from '../../utils/Toast';
import logger from '../../utils/logger';
import { useUser } from '../../utils/UserContext';
import useModerators from '../../utils/useModerators';
import useProfiles from './useProfiles';
import ProfileEditor from './ProfileEditor';

// Built-in cards, shown until an administrator imports them into Firestore
const STATIC_PROFILES = [
  ...content.contacts.map((c, idx) => ({
    id: `static-founder-${idx}`,
    type: 'founder',
    name: c.name,
    title: c.title || '',
    email: c.email || '',
    phone: c.phone || '',
    photoUrl: c.imgUrl || '',
    order: idx * 10,
    visible: true,
  })),
  ...content.moderators.map((m, idx) => ({
    id: `static-moderator-${idx}`,
    type: 'moderator',
    name: m.name,
    title: m.role || '',
    bio: m.bio || '',
    subjects: [],
    photoUrl: m.imgUrl || '',
    order: idx * 10,
    visible: true,
  })),
];

/** Edit/hide/reorder/delete controls shown on a card. */
const CardActions = ({ profile, isAdmin, canEdit, onEdit, onToggle, onMove, onDelete, isFirst, isLast }) => {
  if (!canEdit) return null;
  const btn = 'px-2 py-1 text-xs font-medium rounded-md transition-colors';
  return (
    <div className="flex flex-wrap items-center justify-center gap-1 mt-3 pt-3 border-t border-gray-100 w-full">
      {profile.visible === false && (
        <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 text-[11px] font-medium" title={profile.hiddenReason || ''}>
          Hidden{profile.hiddenReason ? ` · ${profile.hiddenReason}` : ''}
        </span>
      )}
      <button type="button" onClick={onEdit} className={`${btn} text-brand-600 hover:bg-brand-50`}>
        {isAdmin ? 'Edit' : 'Edit my bio'}
      </button>
      {isAdmin && (
        <>
          <button type="button" onClick={onToggle} className={`${btn} text-gray-600 hover:bg-gray-100`}>
            {profile.visible === false ? 'Show' : 'Hide'}
          </button>
          <button type="button" onClick={() => onMove(-1)} disabled={isFirst} className={`${btn} text-gray-600 hover:bg-gray-100 disabled:opacity-30`} aria-label="Move earlier">↑</button>
          <button type="button" onClick={() => onMove(1)} disabled={isLast} className={`${btn} text-gray-600 hover:bg-gray-100 disabled:opacity-30`} aria-label="Move later">↓</button>
          <button type="button" onClick={onDelete} className={`${btn} text-gray-400 hover:text-red-600 hover:bg-red-50`}>Delete</button>
        </>
      )}
    </div>
  );
};

const ContactCard = ({ imgSrc, name, email, phone, title, isFounder, actions }) => {
  const [imgError, setImgError] = useState(false);
  const initials = (name || '?')
    .split(' ')
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="group flex flex-col items-center bg-white p-5 w-64 rounded-xl shadow-md border border-gray-100 transition-all hover:-translate-y-1 hover:shadow-lg">
      {imgSrc && !imgError ? (
        <img
          src={imgSrc}
          alt={name}
          onError={() => setImgError(true)}
          className="w-24 h-24 object-cover rounded-full mb-4 ring-2 ring-blue-100"
        />
      ) : (
        <div className="w-24 h-24 rounded-full mb-4 bg-blue-600 text-white flex items-center justify-center text-xl font-bold ring-2 ring-blue-100">
          {initials}
        </div>
      )}
      <div className="text-center">
        <p className="font-semibold text-gray-900">{name}</p>
        {isFounder && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-1" title="Founder">
            <span>👑</span> Founder
          </span>
        )}
        {title && <p className="text-sm text-gray-500">{title}</p>}
        {email && (
          <p className="mt-1 break-all">
            <a href={`mailto:${email}`} className="text-blue-600 hover:text-blue-700 transition">
              {email}
            </a>
          </p>
        )}
        {phone && <p className="text-gray-500 mt-1">{phone}</p>}
        {email && (
          <div className="mt-3">
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center gap-2 bg-blue-600 text-white font-medium px-3 py-1.5 rounded hover:bg-blue-700 transition"
            >
              Contact
            </a>
          </div>
        )}
      </div>
      {actions}
    </div>
  );
};

function AboutUsPage() {
  const { currentUser, role } = useUser();
  const isAdmin = role === 'admin';
  const uid = currentUser?.uid;
  const { profiles, loading } = useProfiles({ isAdmin, uid });
  const { moderators } = useModerators(isAdmin);

  const [editor, setEditor] = useState(null); // { profile } or { defaultType, nextOrder }
  const [pendingDelete, setPendingDelete] = useState(null);
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', variant: 'success' });
  const notify = (message, variant = 'success') => setToast({ visible: true, message, variant });

  // Until the cards are imported, everyone sees the built-in content
  const usingStatic = !loading && profiles.length === 0;
  const all = usingStatic ? STATIC_PROFILES : profiles;
  const teamCards = all.filter((p) => p.type === 'founder' || p.type === 'team');
  const moderatorCards = all.filter((p) => p.type === 'moderator');
  const founderNames = useMemo(
    () => new Set(all.filter((p) => p.type === 'founder').map((p) => p.name)),
    [all]
  );

  const actor = currentUser?.email || uid || 'unknown';
  const canEdit = (p) =>
    !usingStatic && (isAdmin || (role === 'moderator' && p.type === 'moderator' && p.userId && p.userId === uid));

  const importStatic = async () => {
    setImporting(true);
    try {
      const batch = writeBatch(db);
      STATIC_PROFILES.forEach(({ id, ...card }) => {
        batch.set(doc(collection(db, 'profiles')), {
          ...card,
          photoPath: '',
          userId: null,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          updatedBy: actor,
        });
      });
      await batch.commit();
      notify('Cards imported. You can now edit them here.');
    } catch (err) {
      logger.error('Error importing profiles:', err);
      notify('Could not import the cards.', 'error');
    } finally {
      setImporting(false);
    }
  };

  const toggleVisible = async (p) => {
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'profiles', p.id), {
        visible: p.visible === false,
        hiddenReason: null,
        updatedAt: Timestamp.now(),
        updatedBy: actor,
      });
      await batch.commit();
    } catch (err) {
      logger.error('Error changing visibility:', err);
      notify('Could not change visibility.', 'error');
    }
  };

  // Renumber the section with the card swapped with its neighbour
  const move = async (list, index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    try {
      const batch = writeBatch(db);
      reordered.forEach((p, i) => batch.update(doc(db, 'profiles', p.id), { order: i * 10 }));
      await batch.commit();
    } catch (err) {
      logger.error('Error reordering cards:', err);
      notify('Could not reorder the cards.', 'error');
    }
  };

  const remove = async (p) => {
    try {
      await deleteDoc(doc(db, 'profiles', p.id));
      if (p.photoPath) await deleteObject(ref(storage, p.photoPath)).catch(() => {});
      notify('Card deleted.');
    } catch (err) {
      logger.error('Error deleting card:', err);
      notify('Could not delete the card.', 'error');
    }
  };

  const nextOrder = (list) => (list.length ? Math.max(...list.map((p) => p.order ?? 0)) + 10 : 0);

  const actionsFor = (p, list, index) => (
    <CardActions
      profile={p}
      isAdmin={isAdmin}
      canEdit={canEdit(p)}
      onEdit={() => setEditor({ profile: p })}
      onToggle={() => toggleVisible(p)}
      onMove={(dir) => move(list, index, dir)}
      onDelete={() => setPendingDelete(p)}
      isFirst={index === 0}
      isLast={index === list.length - 1}
    />
  );

  return (
    <>
      <Toast
        message={toast.message}
        variant={toast.variant}
        visible={toast.visible}
        onDismiss={() => setToast((t) => ({ ...t, visible: false }))}
      />
      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete card"
        message={`Delete the card for "${pendingDelete?.name}"? Hiding it instead keeps it for later.`}
        confirmLabel="Delete"
        onConfirm={() => { remove(pendingDelete); setPendingDelete(null); }}
        onCancel={() => setPendingDelete(null)}
      />
      {editor && (
        <ProfileEditor
          profile={editor.profile}
          defaultType={editor.defaultType}
          nextOrder={editor.nextOrder}
          isAdmin={isAdmin}
          moderators={moderators}
          actor={actor}
          onClose={() => setEditor(null)}
          onSaved={(message) => notify(message)}
        />
      )}
      <Helmet>
        <title>About Us - Share2Teach</title>
        <meta name="description" content="Learn more about Share2Teach, our mission to provide open educational resources, and meet our team." />
        <meta name="keywords" content="Share2Teach, open educational resources, education, contributors, team, about us, NWU, NWU Education, NWU ENTERPRISES" />
        <meta property="og:title" content="About Us - Share2Teach" />
        <meta property="og:description" content="Learn more about Share2Teach, our mission to provide open educational resources, and meet our team." />
        <meta property="og:type" content="website" />
      </Helmet>

      <GoogleAnalytics page="AboutUsPage" eventType="aboutus_page" />

      {/* Header */}
      <div className="max-w-6xl mx-auto px-5 mt-6">
        <div className="flex items-center gap-3">
          <span className="inline-block px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">Our story</span>
          <h1 className="text-2xl font-bold text-gray-900">About Share2Teach</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500">Empowering educators with open resources to teach, learn, and inspire.</p>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-5 py-10">
        {/* Mission highlights */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <div className="text-2xl mb-2">🎯</div>
            <h3 className="text-lg font-semibold text-gray-900">Our mission</h3>
            <p className="text-gray-700 mt-1">Enable educators to share, discover, and reuse quality open resources.</p>
          </div>
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <div className="text-2xl mb-2">🤝</div>
            <h3 className="text-lg font-semibold text-gray-900">Community first</h3>
            <p className="text-gray-700 mt-1">Built with and for teachers, lecturers, and learning facilitators.</p>
          </div>
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <div className="text-2xl mb-2">📚</div>
            <h3 className="text-lg font-semibold text-gray-900">Open by design</h3>
            <p className="text-gray-700 mt-1">We embrace OER principles to widen access and impact.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Who we are</h2>
            {content.aboutText.map((para, idx) => (
              <p key={idx} className="text-gray-700 leading-relaxed mb-4">{para}</p>
            ))}
          </div>
          <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Contributing to Share2Teach</h2>
            {content.contributeText.map((para, idx) => (
              <p key={idx} className="text-gray-700 leading-relaxed mb-4">{para}</p>
            ))}
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href="/contribute"
                className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold px-4 py-2 rounded shadow hover:shadow-md hover:bg-blue-700 transition"
              >
                Become a contributor
                <span>→</span>
              </a>
              <a
                href="/self-directed-learning"
                className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition"
              >
                Explore resources
              </a>
            </div>
          </div>
        </div>

        {isAdmin && usingStatic && (
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="flex-1">
              These cards are the built-in content. Import them once to edit, reorder and add cards from this page.
            </p>
            <button
              type="button"
              onClick={importStatic}
              disabled={importing}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60"
            >
              {importing ? 'Importing…' : 'Import cards'}
            </button>
          </div>
        )}

        <div className="text-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">Our team & founders</h2>
          <p className="text-gray-600 mt-1">Leadership and key contacts</p>
          {isAdmin && !usingStatic && (
            <button
              type="button"
              onClick={() => setEditor({ defaultType: 'team', nextOrder: nextOrder(teamCards) })}
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              + Add team or founder card
            </button>
          )}
        </div>
        {/* Center the grid block and cards (two founders centered) */}
        <div className="flex justify-center">
          <div className="inline-grid grid-cols-1 sm:grid-cols-2 gap-6">
          {teamCards.map((p, idx) => (
            <ContactCard
              key={p.id}
              imgSrc={p.photoUrl}
              name={p.name}
              email={p.email}
              phone={p.phone}
              title={p.title}
              isFounder={p.type === 'founder'}
              actions={actionsFor(p, teamCards, idx)}
            />
          ))}
          </div>
        </div>

        {/* Moderators */}
        <div className="mt-14">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Moderator bios</h2>
            <p className="text-gray-600 mt-1">Meet the moderators who curate, review, and champion quality resources across subjects. They guide community contributions, uphold academic rigour, and help educators discover impactful materials.</p>
            {isAdmin && !usingStatic && (
              <button
                type="button"
                onClick={() => setEditor({ defaultType: 'moderator', nextOrder: nextOrder(moderatorCards) })}
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                + Add moderator card
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {moderatorCards.map((p, idx) => (
              <ModeratorCard
                key={p.id}
                name={p.name}
                role={p.title}
                imgSrc={p.photoUrl}
                bio={p.bio}
                subjects={p.subjects}
                isFounder={founderNames.has(p.name)}
                actions={actionsFor(p, moderatorCards, idx)}
              />
            ))}
          </div>
        </div>

      </div>
    </>
  );
}

export default AboutUsPage;

// Local components last for clarity
const ModeratorCard = ({ imgSrc, name, role, bio, subjects = [], isFounder, actions }) => {
  const [imgError, setImgError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initials = (name || '?')
    .split(' ')
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden flex flex-col">
      <div className="p-5 flex items-start gap-4">
        {imgSrc && !imgError ? (
          <img
            src={imgSrc}
            alt={name}
            onError={() => setImgError(true)}
            className="w-20 h-20 object-cover rounded-full ring-2 ring-blue-100 flex-shrink-0"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-bold ring-2 ring-blue-100 flex-shrink-0">
            {initials}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{name}</h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800" title="Moderator">
              <span>🛡️</span> Moderator
            </span>
            {isFounder && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" title="Founder">
                <span>👑</span> Founder
              </span>
            )}
          </div>
          {role && <p className="text-sm text-gray-600 mt-0.5">{role}</p>}
          {subjects.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {subjects.map((s) => (
                <span key={s} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px]">{s}</span>
              ))}
            </div>
          )}

          {/* Preview (clamped) */}
          <div className="mt-3 mlc-fade-bottom">
            <p className="text-gray-700 mlc-line-clamp-3">{bio}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Read bio <span aria-hidden>→</span>
          </button>
        </div>
      </div>
      {actions && <div className="px-5 pb-4 -mt-2">{actions}</div>}

      {/* Modal for full bio */}
      {showModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowModal(false)} />
          <div role="dialog" aria-modal="true" className="relative z-[1001] w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-start gap-4 p-6 border-b border-gray-100">
              {imgSrc && !imgError ? (
                <img src={imgSrc} alt={name} className="w-16 h-16 object-cover rounded-full ring-2 ring-blue-100" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-bold ring-2 ring-blue-100">
                  {initials}
                </div>
              )}
              <div className="min-w-0 flex-1 pr-8">
                <h3 className="text-xl font-semibold text-gray-900">{name}</h3>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  {role && <p className="text-sm text-gray-600">{role}</p>}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800" title="Moderator">
                    <span>🛡️</span> Moderator
                  </span>
                  {isFounder && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" title="Founder">
                      <span>👑</span> Founder
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="absolute top-3 right-3 inline-flex items-center justify-center h-9 w-9 rounded-full text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-auto">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{bio}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
